
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import BottomNavigation from '../../components/feature/BottomNavigation';

interface CartItem {
  id: number;
  name: string;
  price: number;
  image: string;
  quantity: number;
}

export default function CartPage() {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);

  useEffect(() => {
    // تحميل السلة من localStorage
    const savedCart = localStorage.getItem('cartItems');
    if (savedCart) {
      setCartItems(JSON.parse(savedCart));
    }
  }, []);

  const updateCart = (newCartItems: CartItem[]) => {
    setCartItems(newCartItems);
    localStorage.setItem('cartItems', JSON.stringify(newCartItems));
  };

  const updateQuantity = (id: number, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeItem(id);
      return;
    }
    
    const updatedItems = cartItems.map(item =>
      item.id === id ? { ...item, quantity: newQuantity } : item
    );
    updateCart(updatedItems);
  };

  const removeItem = (id: number) => {
    const updatedItems = cartItems.filter(item => item.id !== id);
    updateCart(updatedItems);
    setShowDeleteConfirm(null);
  };

  const clearCart = () => {
    updateCart([]);
  };

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const handleCheckout = () => {
    if (cartItems.length > 0) {
      navigate('/checkout');
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header cartItemsCount={0} />
        
        <main className="pt-20 pb-20 px-4">
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-shopping-cart-line text-4xl text-[#D4AF37]"></i>
            </div>
            <h2 className="text-xl font-bold text-[#333333] mb-4" dir="rtl">
              السلة فارغة
            </h2>
            <p className="text-[#777777] mb-8" dir="rtl">
              لم تقم بإضافة أي منتجات إلى السلة بعد
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-[#D4AF37] text-white px-8 py-3 rounded-full font-medium"
            >
              تصفح المنتجات
            </button>
          </div>
        </main>

        <BottomNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header cartItemsCount={getTotalItems()} />
      
      {/* تأكيد الحذف */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-[#333333] mb-4 text-center" dir="rtl">
              تأكيد الحذف
            </h3>
            <p className="text-[#777777] text-center mb-6" dir="rtl">
              هل تريد حذف هذا المنتج من السلة؟
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => removeItem(showDeleteConfirm)}
                className="flex-1 bg-red-500 text-white py-3 rounded-lg font-medium"
              >
                حذف
              </button>
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 bg-gray-200 text-[#333333] py-3 rounded-lg font-medium"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
      
      <main className="pt-20 pb-32 px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6" dir="rtl">
          <h1 className="text-2xl font-bold text-[#333333]">
            سلة المشتريات
          </h1>
          <button
            onClick={clearCart}
            className="text-red-500 text-sm font-medium flex items-center"
          >
            <i className="ri-delete-bin-line ml-1"></i>
            إفراغ السلة
          </button>
        </div>

        {/* Cart Items */}
        <div className="space-y-4 mb-6">
          {cartItems.map((item) => (
            <div key={item.id} className="bg-white rounded-xl p-4 shadow-md">
              <div className="flex gap-4" dir="rtl">
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-20 h-20 object-cover rounded-lg"
                />
                
                <div className="flex-1">
                  <h3 className="font-semibold text-[#333333] text-sm mb-2">
                    {item.name}
                  </h3>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="w-8 h-8 bg-[#D4AF37]/10 rounded-full flex items-center justify-center"
                      >
                        <i className="ri-subtract-line text-[#D4AF37]"></i>
                      </button>
                      
                      <span className="font-medium text-[#333333] min-w-[2rem] text-center">
                        {item.quantity}
                      </span>
                      
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="w-8 h-8 bg-[#D4AF37]/10 rounded-full flex items-center justify-center"
                      >
                        <i className="ri-add-line text-[#D4AF37]"></i>
                      </button>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-[#4B2E05]">
                        {(item.price * item.quantity).toFixed(0)} ر.س
                      </span>
                      
                      <button
                        onClick={() => setShowDeleteConfirm(item.id)}
                        className="w-8 h-8 bg-red-50 rounded-full flex items-center justify-center"
                      >
                        <i className="ri-delete-bin-line text-red-500 text-sm"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div className="bg-white rounded-xl p-6 shadow-md">
          <h3 className="text-lg font-bold text-[#333333] mb-4 text-right" dir="rtl">
            ملخص الطلب
          </h3>
          
          <div className="space-y-3 mb-4" dir="rtl">
            <div className="flex justify-between">
              <span className="text-[#777777]">المجموع الفرعي</span>
              <span className="font-medium">{getTotalPrice().toFixed(0)} ر.س</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-[#777777]">رسوم التوصيل</span>
              <span className="font-medium text-green-600">مجاني</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-[#777777]">الضريبة (15%)</span>
              <span className="font-medium">{(getTotalPrice() * 0.15).toFixed(0)} ر.س</span>
            </div>
            
            <div className="border-t pt-3">
              <div className="flex justify-between">
                <span className="text-lg font-bold text-[#333333]">المجموع الكلي</span>
                <span className="text-lg font-bold text-[#4B2E05]">
                  {(getTotalPrice() * 1.15).toFixed(0)} ر.س
                </span>
              </div>
            </div>
          </div>
          
          <button
            onClick={handleCheckout}
            className="w-full bg-[#D4AF37] text-white py-4 rounded-lg font-bold text-lg hover:bg-[#B8941F] transition-colors duration-200"
          >
            متابعة إلى الدفع
          </button>
        </div>

        {/* Delivery Info */}
        <div className="bg-[#D4AF37]/10 rounded-xl p-4 mt-4">
          <div className="flex items-center gap-3" dir="rtl">
            <div className="w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center">
              <i className="ri-truck-line text-white"></i>
            </div>
            <div>
              <h4 className="font-semibold text-[#333333] text-sm">
                توصيل مجاني
              </h4>
              <p className="text-xs text-[#777777]">
                للطلبات أكثر من 100 ر.س
              </p>
            </div>
          </div>
        </div>
      </main>

      <BottomNavigation />
    </div>
  );
}
